import { isDate, isValidDate as isValidDateUtil } from '#utils/date';
import { isEmpty, isArray } from '#utils/array';
import { camelToSnakeCase, isString, snakeToCamelCase } from '#utils/string';

export const isObject = value => typeof value === 'object';

export const isEmptyObject = object => !Object.keys(object).length;

export const isValidDate = date => !isNaN(date) && date instanceof Date;

const isComplexObject = obj => isObject(obj) && !Array.isArray(obj) && !isDate(obj);

export const removeKeys = (object, condition) => {
  if (isObject(object) && !Array.isArray(object)) {
    const newObject = {};
    Object.keys(object)
      .filter(key => !condition(object[key]))
      .forEach(key => {
        newObject[key] = removeKeys(object[key], condition);
      });
    return newObject;
  }
  return object;
};

export const removeFalsyValues = object => removeKeys(object, value => !value);

export const getIfExist = elem => (elem ? elem : undefined);

export const filterKeysByCondition = (object, condition) =>
  Object.fromEntries(Object.entries(object).filter(condition));

export const filterNonAdmittedKeys = (object, nonAdmittedKeys) =>
  filterKeysByCondition(object, ([key]) => !nonAdmittedKeys.includes(key));

export const omitKeys = (obj, keys) => {
  if (!obj) {
    return {};
  }
  if (!keys || keys.length === 0) {
    return obj;
  }
  return keys.reduce(
    (acc, key) => {
      delete acc[key];
      return acc;
    },
    { ...obj }
  );
};

export const reverseObjectOrder = object =>
  Object.keys(object)
    .reverse()
    .reduce((accumulator, key) => {
      accumulator[key] = object[key];
      return accumulator;
    }, {});

export const removeEmptyObjects = data =>
  Object.fromEntries(
    Object.entries(data).filter(([, value]) => value && (isValidDateUtil(value) || !isEmptyObject(value)))
  );

export const removeEmptyValues = data => {
  const newObject = {};
  Object.entries(data).forEach(([key, value]) => {
    if (value) {
      newObject[key] = value;
    }
  });
  return newObject;
};

export const areEqualObjects = (object1, object2) =>
  Object.keys(object1).every(key =>
    isObject(object1[key]) ? areEqualObjects(object1[key], object2[key]) : object1[key] === object2[key]
  );

export const getObjectsValues = objects =>
  Object.entries(objects).reduce(
    (acc, [field, { values }]) =>
      acc.concat(Object.entries(values).map(([id, { label }]) => ({ field, id, label }))),
    []
  );

export const compareIds = id => object => object.id === id;

export const objectMap = (obj, fn) =>
  Object.fromEntries(Object.entries(obj).map(([k, v], i) => [k, fn(v, k, i)]));

export const mapAllObjectValues = (object, mapFunction) =>
  Object.fromEntries(
    Object.entries(object).map(([key, value]) => [
      key,
      Array.isArray(value) && !isEmpty(value)
        ? value.map(item => (isComplexObject(item) ? mapAllObjectValues(item, mapFunction) : item))
        : isComplexObject(value)
        ? mapAllObjectValues(value, mapFunction)
        : mapFunction(value)
    ])
  );

const changeCaseObject = ({ originalObject, caseFunction, nestedCaseFunction }) => {
  const newObject = {};
  Object.entries(originalObject).forEach(([key, value]) => {
    newObject[caseFunction(key)] =
      isObject(value) && Object.keys(value).length ? nestedCaseFunction(value.dataValues) : value;
  });
  return newObject;
};

export const objectToCamelCase = snakeCaseObject =>
  changeCaseObject({
    originalObject: snakeCaseObject,
    caseFunction: snakeToCamelCase,
    nestedCaseFunction: objectToCamelCase
  });

export const objectToSnakeCase = camelCaseObject =>
  changeCaseObject({
    originalObject: camelCaseObject,
    caseFunction: camelToSnakeCase,
    nestedCaseFunction: objectToSnakeCase
  });

export const generatePKObject = (list, primaryKey) => {
  const object = {};
  list.forEach(elem => {
    object[elem[primaryKey]] = elem;
  });
  return object;
};

export const findKeyByValue = (object, value) => Object.keys(object).find(key => object[key] === value);

export const deleteUndefined = object => {
  const newObject = { ...object };
  Object.keys(newObject).forEach(key => newObject[key] === undefined && delete newObject[key]);
  return newObject;
};

export const removeEmptyKeys = object => {
  const values = [undefined, null];
  if (isObject(object) && !Array.isArray(object)) {
    const newObject = {};
    Object.keys(object)
      .filter(key => !values.includes(object[key]))
      .forEach(key => {
        newObject[key] = removeEmptyKeys(object[key]);
      });
    return newObject;
  }
  return object;
};

const reduceObject = (element, reducer) => {
  if (!isObject(element) || !element) {
    return element;
  }
  const elementCopy = { ...element };
  return Object.keys(element).reduce((acum, key) => reducer(acum, key, elementCopy), {});
};

export const objectTrim = values =>
  reduceObject(values, (accumulator, key) => {
    accumulator[key] = isString(values[key]) ? values[key].trim() : objectTrim(values[key]);
    return accumulator;
  });

export const deleteNull = values =>
  reduceObject(values, (accumulator, key) => {
    if (values[key] !== null) {
      accumulator[key] = deleteNull(values[key]);
    }
    return accumulator;
  });

export const assignValueToAllKeys = (object, value) =>
  reduceObject(object, (accumulator, key) => ({ ...accumulator, [key]: value }));

export const merge = objects => {
  const newObject = {};
  objects.map(object => Object.assign(newObject, object));
  return newObject;
};

export const convertToDotNotation = (obj, newObj = {}, prefix = '') => {
  Object.keys(obj).forEach(key => {
    if (isObject(obj[key]) && !isArray(obj[key])) {
      convertToDotNotation(obj[key], newObj, `${prefix + key}.`);
    } else {
      newObj[prefix + key] = obj[key];
    }
  });
  return newObj;
};

export const getComposedKeyValue = (object, keys) =>
  keys.reduce((obj, key) => (obj && obj[key] !== 'undefined' ? obj[key] : undefined), object);

export const parseObject = obj => {
  if (!obj) {
    return null;
  }
  if (typeof result === 'object') {
    return obj;
  }
  try {
    return JSON.parse(obj);
  } catch {
    return null;
  }
};
